/**
 * OUP-2 — take the user to the field that is wrong.
 *
 * A 422 arrives keyed by the API's field names (`city_id`, `postal_code`,
 * `new_manager.email`). The inputs are keyed by their own element ids. This
 * maps one to the other, then scrolls the first offending field into view and
 * focuses it, so a long form does not simply refuse to submit with the reason
 * somewhere below the fold.
 *
 * Order matters and is the caller's: the map is walked in insertion order, not
 * the order the API happened to list its errors in, so the user is sent to the
 * first *visually* invalid field rather than whichever key Laravel emitted
 * first.
 */

/** Scrolls to and focuses one element by id. Returns whether it found one. */
const focusById = (id) => {
  const el = document.getElementById(id);
  if (!el) return false;

  // `block: "center"` rather than the default: a field pinned to the very top
  // of the viewport sits under the sticky header on smaller screens.
  el.scrollIntoView({ behavior: "smooth", block: "center" });

  // Focus after the scroll is under way. Focusing first makes the browser jump
  // instantly and then fight the smooth scroll.
  window.setTimeout(() => {
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }, 150);

  return true;
};

/**
 * @param {Record<string, string[]>} errors  the 422 map, keyed by API field
 * @param {Record<string, string>} fieldMap  API key -> element id, in form order
 * @returns {string|undefined} the API key that was focused, if any
 */
export const focusFirstError = (errors, fieldMap) => {
  if (!errors || typeof document === "undefined") return undefined;

  for (const [apiKey, elementId] of Object.entries(fieldMap)) {
    // `services.0.starts_at` and friends: a prefix match catches the whole
    // group so a list error still lands on the control that owns the list.
    const hit = Object.keys(errors).find((key) => key === apiKey || key.startsWith(`${apiKey}.`));

    if (hit && focusById(elementId)) return hit;
  }

  // An error with nowhere to land — a plan limit, a tenancy refusal. The caller
  // still has its banner; returning undefined tells it nothing was focused.
  return undefined;
};
